import { Pagination } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Code } from 'src/design/atoms/Code';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Drawer } from 'src/design/molecules/Drawer';
import { Table } from 'src/design/molecules/Table';
import {
  IconChevronDown,
  IconChevronRight,
  IconInfoCircle,
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import { ScimIntegrationDto } from 'src/api/FiretrolApiClient';
import {
  SCIM_LOG_PAGE_SIZE as PAGE_SIZE,
  useScimLogDetail,
  useScimLogs,
} from 'src/api/queries/admin/useScimIntegrations';
import { formatDateTime, formatJsonString } from 'src/utils';

interface ScimLogsDrawerProps {
  integration: ScimIntegrationDto | null;
  onClose: () => void;
}

export const ScimLogsDrawer: React.FC<ScimLogsDrawerProps> = ({
  integration,
  onClose,
}) => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const integrationId = integration?.id ?? null;
  const logsQuery = useScimLogs(integrationId, page);
  const logs = logsQuery.data?.logs ?? [];
  const totalCount = logsQuery.data?.totalCount ?? 0;
  const loading = logsQuery.isFetching;

  const detailQuery = useScimLogDetail(integrationId, expandedId);
  const detail = detailQuery.data;
  const detailLoading = detailQuery.isLoading;

  // Reset state when integration changes
  useEffect(() => {
    setPage(1);
    setExpandedId(null);
  }, [integrationId]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const toggleExpand = (logId: string) =>
    setExpandedId((current) => (current === logId ? null : logId));

  return (
    <Drawer
      opened={!!integration}
      onClose={onClose}
      title={`Audit Logs — ${integration?.name || ''}`}
      position="right"
      size="70%"
    >
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />

        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Logs are retained for 90 days. Older entries are automatically
          removed. Click a row to view request/response details.
        </Alert>

        {logs.length === 0 && !loading && (
          <Text c="dimmed" ta="center" mt="lg">
            No audit logs found for this integration.
          </Text>
        )}

        {logs.length > 0 && (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={20} />
                  <Table.Th>Time</Table.Th>
                  <Table.Th>User</Table.Th>
                  <Table.Th>Operation</Table.Th>
                  <Table.Th>Method</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Result</Table.Th>
                  <Table.Th>Error</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <Table.Tr
                      onClick={() => toggleExpand(log.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>
                        {expandedId === log.id ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{formatDateTime(log.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{log.userEmail || '—'}</Text>
                      </Table.Td>
                      <Table.Td>{log.operation}</Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {log.httpMethod}
                        </Text>
                      </Table.Td>
                      <Table.Td>{log.responseStatus ?? '—'}</Table.Td>
                      <Table.Td>
                        <Badge
                          color={log.succeeded ? 'green' : 'red'}
                          size="sm"
                        >
                          {log.succeeded ? 'OK' : 'FAIL'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {log.errorMessage && (
                          <Text size="xs" c="red" lineClamp={2}>
                            {log.errorMessage}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                    {expandedId === log.id && (
                      <Table.Tr>
                        <Table.Td colSpan={8} p={0}>
                          <Stack gap="xs" p="sm" bg="gray.0">
                            <Text size="xs" fw={600} ff="monospace">
                              {log.httpMethod} {log.requestUrl}
                            </Text>
                            {detailLoading && (
                              <Group justify="center" py="md">
                                <Loader size="sm" />
                              </Group>
                            )}
                            {!detailLoading && detail && (
                              <>
                                {detail.requestBody && (
                                  <>
                                    <Text size="xs" fw={600}>
                                      Request Body
                                    </Text>
                                    <Code
                                      block
                                      style={{
                                        maxHeight: 300,
                                        overflow: 'auto',
                                      }}
                                    >
                                      {formatJsonString(detail.requestBody)}
                                    </Code>
                                  </>
                                )}
                                {detail.responseBody && (
                                  <>
                                    <Text size="xs" fw={600}>
                                      Response Body
                                    </Text>
                                    <Code
                                      block
                                      style={{
                                        maxHeight: 300,
                                        overflow: 'auto',
                                      }}
                                    >
                                      {formatJsonString(detail.responseBody)}
                                    </Code>
                                  </>
                                )}
                                {!detail.requestBody && !detail.responseBody && (
                                  <Text size="xs" c="dimmed">
                                    No request/response body recorded.
                                  </Text>
                                )}
                              </>
                            )}
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </React.Fragment>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};
